import useCommonStore, { ModalConfig } from '@/stores/common';
import React, { useMemo } from 'react';
import { useScheduleLessonForm } from '@/components/Common/Modals/ScheduleLessonModal/useScheduleLessonForm';
import { useTimeOptions } from '@/components/Common/Modals/ScheduleLessonModal/useTimeOptions';
import ModalHeader from '@/components/Common/Modals/ScheduleLessonModal/ModalHeader';
import FormSections from '@/components/Common/Modals/ScheduleLessonModal/FormSections';
import ActionButtons from '@/components/Common/Modals/ScheduleLessonModal/ActionButtons';
import InstructorView from '@/components/Common/Modals/ScheduleLessonModal/InstructorView';
import { useAuthStore } from '@/stores';
import { ROLE_SLUG } from '@/constants';

const ScheduleLessonModal: React.FC = () => {
  const { modalConfig = {} as ModalConfig, hideCommonModal } = useCommonStore<{
    modalConfig?: ModalConfig;
    hideCommonModal: () => void;
  }>(state => state);
  const lesson = useMemo(() => {
    return (modalConfig?.data?.lesson ?? {}) as Record<string, unknown>;
  }, [modalConfig?.data?.lesson]);

  const isEditing = useMemo(() => {
    const hasLessonObject = lesson && Object.keys(lesson).length > 0;
    const hasLessonId = Boolean((modalConfig?.data as any)?.lessonId);
    return hasLessonObject || hasLessonId;
  }, [lesson, modalConfig?.data]);

  // isInstructor
  const userInfo = useAuthStore(state => state.userInfo);
  const roleData = userInfo?.user?.role as unknown;
  const roleSlug =
    (typeof roleData === 'object' && roleData !== null
      ? (roleData as { slug?: string }).slug
      : null) ?? undefined;
  const isInstructor = roleSlug === ROLE_SLUG?.INSTRUCTOR;

  // Use the custom hook for all form logic
  const {
    control,
    handleSubmit,
    isSubmitting,
    watch,
    instructors,
    serviceOptionsFromQuery,
    hotels,
    agent,
    cars,
    mountains,
    CreateArrayOptions,
    isPaidInitial,
    selectedStart,
    onSubmit,
    toMinutes,
    hasClipboard,
    confirmCopyForm,
    confirmPasteForm,
    ConfirmModalUI,
    DuplicateBookingModalUI,
    confirmDeleteLesson,
    confirmProcessAndUnprocessedLessons,
    lessonsEntry
  } = useScheduleLessonForm(modalConfig, lesson, isEditing, () =>
    hideCommonModal()
  );

  // Get time options
  const { timeOptions, pickupTimeOptions } = useTimeOptions();

  // Filter pickup time options based on selected start time
  const filteredPickupTimeOptions = useMemo(() => {
    if (!selectedStart) return pickupTimeOptions;
    const startMinutes = toMinutes(selectedStart);
    return pickupTimeOptions.filter(
      (opt: { value: string }) => toMinutes(opt.value) <= startMinutes
    );
  }, [pickupTimeOptions, selectedStart, toMinutes]);

  // Handle form submission
  const handleFormSubmit = async (data: any) => {
    const ok = await onSubmit(data);
    if (ok) hideCommonModal();
  };

  // Handle close
  const handleClose = () => {
    if (modalConfig?.onClose) {
      modalConfig.onClose();
    } else {
      hideCommonModal();
    }
  };

  // Handle delete (trigger confirm only; hook will close on success)
  const handleDelete = () => {
    confirmDeleteLesson();
  };

  // Handle process toggle (trigger confirm only; hook will close on success)
  const handleProcessToggle = () => {
    confirmProcessAndUnprocessedLessons();
  };

  return (
    <div className='px-4 pb-3'>
      {ConfirmModalUI}
      {!isInstructor && DuplicateBookingModalUI}

      <ModalHeader
        control={control}
        isPaidInitial={isPaidInitial}
        isDisabled={isInstructor}
        title={modalConfig?.data?.title}
      />

      {isInstructor ? (
        <InstructorView
          values={watch()}
          instructors={instructors}
          serviceOptions={serviceOptionsFromQuery}
          hotels={hotels}
          cars={cars}
          mountains={mountains}
          agents={agent}
          lessonsEntry={lessonsEntry}
        />
      ) : (
        <form onSubmit={handleSubmit(handleFormSubmit)} className='space-y-4'>
          <FormSections
            control={control}
            isPaidInitial={isPaidInitial}
            instructors={instructors}
            serviceOptions={serviceOptionsFromQuery}
            hotels={hotels}
            cars={cars}
            mountains={mountains}
            agents={agent}
            timeOptions={timeOptions}
            pickupTimeOptions={filteredPickupTimeOptions}
            commentsLength={(watch('comments') as string)?.length || 0}
            CreateArrayOptions={CreateArrayOptions}
            lessonsEntry={lessonsEntry}
          />
          <ActionButtons
            isEditing={isEditing}
            isPaidInitial={isPaidInitial}
            isSubmitting={isSubmitting}
            hasClipboard={hasClipboard}
            onClose={handleClose}
            onCopy={confirmCopyForm}
            onPaste={confirmPasteForm}
            onDelete={handleDelete}
            onProcessToggle={handleProcessToggle}
            onSubmit={() => {}}
          />
        </form>
      )}
    </div>
  );
};

export default ScheduleLessonModal;
